import React from 'react';
import { Box, Typography } from '@mui/material';
import Skeleton from 'react-loading-skeleton';
import { Invoice } from 'types/Invoice';
import { useInvoicesContext } from './InvoicesContext';
import NoInvoicesImage from 'assets/no_invoices.svg';
import { NoItemsPlaceholder } from 'components';
import {
  DateCell,
  InvoiceActionCell,
  InvoiceAmountCell,
  InvoiceCampaignCell,
  InvoiceLinkCell,
  InvoicesStatusCell,
} from './InvoicesTableCells';

const columns = [
  {
    label: 'Rechnungsdatum',
    Cell: (invoice: Invoice) => <DateCell invoice={invoice} />,
    minWidth: 150,
    maxWidth: '15%',
    flex: 1,
  },
  {
    label: 'Rechnungsnr.',
    Cell: (invoice: Invoice) => <InvoiceLinkCell invoice={invoice} />,
    minWidth: 100,
    maxWidth: '12%',
    flex: 0.8,
  },
  {
    label: 'Kampagne',
    Cell: (invoice: Invoice) => <InvoiceCampaignCell invoice={invoice} />,
    minWidth: 250,
    maxWidth: '31%',
    flex: 1.9,
  },
  {
    label: 'Betrag',
    Cell: (invoice: Invoice) => <InvoiceAmountCell invoice={invoice} />,
    minWidth: 80,
    maxWidth: '10%',
    flex: 0.6,
  },
  {
    label: 'Status',
    Cell: (invoice: Invoice) => <InvoicesStatusCell invoice={invoice} />,
    minWidth: 70,
    maxWidth: '10%',
    flex: 0.8,
  },
  {
    label: '',
    Cell: (invoice: Invoice) => <InvoiceActionCell invoice={invoice} />,
    minWidth: 200,
    maxWidth: '14%',
    flex: 0.9,
  },
];

const TableRowSkeleton = () => {
  return (
    <Box sx={{ py: 5, display: 'flex', gap: 2 }}>
      {columns.map((column, index) => (
        <Box key={index} sx={{ minWidth: column.minWidth, maxWidth: column.maxWidth, flex: column.flex }}>
          <Skeleton height={64} />
        </Box>
      ))}
    </Box>
  );
};

export const InvoicesTable = () => {
  const { invoices, invoicesLoading, filtersApplied, resetFilters } = useInvoicesContext();
  return (
    <Box sx={{ overflowX: 'auto' }}>
      {/* add all columns minWidth + 100px gap for ActionCell in order to preserve table width and allow scroll on mobile*/}
      <Box sx={{ minWidth: columns.reduce((sum, col) => sum + col.minWidth, 0) + 100 }}>
        <Box sx={{ backgroundColor: 'grey.200', py: 3, px: 5, borderRadius: '12px 12px 0 0 ' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {columns.map((column) => (
              <Box key={column.label} sx={{ minWidth: column.minWidth, maxWidth: column.maxWidth, flex: column.flex }}>
                <Typography fontWeight="bold">{column.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ backgroundColor: 'grey.50', borderRadius: '0 0 12px 12px', px: 5 }}>
          {invoicesLoading ? Array.from({ length: 10 }, (_, index) => <TableRowSkeleton key={index} />) : null}
          {filtersApplied && invoices.length === 0 ? (
            <NoItemsPlaceholder
              image={NoInvoicesImage}
              imageAlt="No invoices"
              title="Du hast keine Rechnungen, die dieser Suchanfrage entsprechen"
              button={{
                type: 'contained',
                text: 'Filter löschen',
                onClick: () => resetFilters(),
              }}
            />
          ) : null}
          {invoices.map((invoice) => (
            <Box
              key={invoice.id}
              sx={{
                py: 5,
                borderBottom: (theme) => `1px solid ${theme.palette.grey[300]}`,
                display: 'flex',
                gap: 2,
              }}
            >
              {columns.map((column) => (
                <Box
                  key={`${invoice.id}-${column.label}`}
                  sx={{ minWidth: column.minWidth, maxWidth: column.maxWidth, flex: column.flex }}
                >
                  {column.Cell(invoice)}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
